import { useEffect, useRef, useState } from 'react';
import { useSessionContext } from '@contexts/SessionContext';
import { useResumeDetection } from '@hooks/useResumeDetection';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { resolveNavStepRoute } from '@constants/routes';
import { ROUTES } from '@constants/routes';
import { qualificationRouter } from '@utils/qualificationRouter/qualificationRouter';
import { LeadCaptureReason } from '@t/serviceQualification.types';
import { CPQ_ORDER_DATA_KEY } from '@t/session.types';

interface InitialRouteResult {
  /** The resolved initial route for MemoryRouter, null while still loading */
  initialRoute: string | null;
}

/**
 * Resolves the correct initial route based on session state and resume detection.
 * Runs outside the router so MemoryRouter can start at the correct path — no flash.
 *
 * Also sets qualification flow state (hasActiveWorkOrder, resumeDetectionFailed, resumeRoute)
 * for downstream components.
 */
export const useInitialRoute = (defaultRoute: string): InitialRouteResult => {
  const { isInitializing, hasValidSession, sessionId, currentStep } = useSessionContext();
  const { isChecking, isError, hasActiveWorkOrder, needsResume } = useResumeDetection(sessionId);
  const setQualificationFlow = useQualificationFlowStore((s) => s.setQualificationFlow);
  const qualificationResult = useQualificationFlowStore((s) => s.qualificationResult);
  const leadCaptureReason = useQualificationFlowStore((s) => s.leadCaptureReason);

  const [initialRoute, setInitialRoute] = useState<string | null>(null);
  const didResolve = useRef(false);

  const dataReady = !isInitializing && !isChecking;

  useEffect(() => {
    if (!dataReady || didResolve.current) return;
    didResolve.current = true;

    // Active work order — stay on default route, show banner
    if (hasActiveWorkOrder) {
      setQualificationFlow((prev) => ({
        ...prev,
        hasActiveWorkOrder: true,
      }));
      setInitialRoute(defaultRoute);
      return;
    }

    // Resume detection failed — stay on default route, show error banner
    if (isError) {
      setQualificationFlow((prev) => ({
        ...prev,
        resumeDetectionFailed: true,
      }));
      setInitialRoute(defaultRoute);
      return;
    }

    // No valid session — use default route
    if (!hasValidSession || !currentStep) {
      setInitialRoute(defaultRoute);
      return;
    }

    const targetRoute = resolveNavStepRoute(currentStep);

    // CHECKOUT / CONFIRMATION are backend-authoritative — the user has progressed past
    // qualification, so a stale LEAD_CAPTURE qualificationResult must not override them.
    if (targetRoute === ROUTES.CHECKOUT) {
      if (needsResume) {
        setQualificationFlow((prev) => ({ ...prev, resumeRoute: targetRoute }));
      }
      setInitialRoute(targetRoute);
      return;
    }

    if (targetRoute === ROUTES.DONE) {
      const hasOrderData = Boolean(localStorage.getItem(CPQ_ORDER_DATA_KEY));
      setInitialRoute(hasOrderData ? targetRoute : defaultRoute);
      return;
    }

    // LEAD_CAPTURE on refresh — `leadCaptureStatus` is transient (not persisted), so a
    // refresh after the user dismissed the lead form drops back to ProductOffersPage with
    // no dialog. Re-derive routing from the persisted qualification state and mark
    // leadCaptureStatus as 'dismissed' so ProductOffersPage's existing showCheckAvailability
    // logic re-opens CheckAvailabilityDialog. ADDRESS_NOT_FOUND keeps qualificationResult
    // null while setting leadCaptureReason — handle it via the reason fallback.
    // SYSTEM_ERROR is deliberately excluded.
    const isLeadCaptureRouting =
      (qualificationResult != null && qualificationRouter(qualificationResult).destination === 'LEAD_CAPTURE') ||
      leadCaptureReason === LeadCaptureReason.ADDRESS_NOT_FOUND;
    if (isLeadCaptureRouting) {
      setQualificationFlow((prev) => ({ ...prev, leadCaptureStatus: 'dismissed' }));
    }

    // Unknown step — use default route
    if (!targetRoute) {
      setInitialRoute(defaultRoute);
      return;
    }

    // SELECT → go to the correct page
    // Only show the resume modal if there's actually something to resume (cart has items)
    if (needsResume) {
      setQualificationFlow((prev) => ({ ...prev, resumeRoute: targetRoute }));
    }
    setInitialRoute(targetRoute);
  }, [
    dataReady,
    hasValidSession,
    currentStep,
    hasActiveWorkOrder,
    isError,
    qualificationResult,
    leadCaptureReason,
    setQualificationFlow,
    defaultRoute,
  ]);

  return { initialRoute };
};
